/* eslint-disable @typescript-eslint/no-explicit-any */
import { AuthProps, DataAuthProps, LocalStorageProps } from '@/@types/auth'
import { AccountType } from '@/pages/auth/components/user-auth-form'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

export default function useLocalStorage<T>({
  key,
  defaultValue,
}: LocalStorageProps<T>) {
  const [value, setValue] = useState<T>(() => {
    const storedValue = localStorage.getItem(key)
    
    // Check if storedValue is null, "undefined" string, or empty
    if (storedValue === null || storedValue === 'undefined' || storedValue === '') {
      return defaultValue
    }
    
    try {
      return JSON.parse(storedValue) as T
    } catch (error) {
      console.warn(`Failed to parse localStorage value for key "${key}":`, error)
      return defaultValue
    }
  })

  useEffect(() => {
    localStorage.setItem(key, JSON.stringify(value))
  }, [value, key])

  return [value, setValue] as const
}

export const useShortCode = () => {
  const [shortCode, setShortCode] = useState<string>(() => {
    const vbAuth = localStorage.getItem('vbAuth')

    
    // Check for null, "undefined" string, or empty
    if (!vbAuth || vbAuth === 'undefined' || vbAuth === '') {
      return ''
    }
    
    try {
      const parsed = JSON.parse(vbAuth)
      return (parsed as DataAuthProps)?.data?.venue?.short_code ?? 
             (parsed?.venue?.short_code as AuthProps) ?? 
             ''
    } catch (error) {
      console.warn('Failed to parse vbAuth:', error)
      return ''
    }
  })

  useEffect(() => {
    const handleStorageChange = () => {
      const vbAuth = localStorage.getItem('vbAuth')
      
      if (!vbAuth || vbAuth === 'undefined' || vbAuth === '') {
        setShortCode('')
        return
      }
      
      try {
        const parsed = JSON.parse(vbAuth)
        const newShortCode = (parsed as DataAuthProps)?.data?.venue?.short_code ?? 
                           (parsed?.venue?.short_code as AuthProps) ?? 
                           ''
        setShortCode(newShortCode)
      } catch (error) {
        console.warn('Failed to parse vbAuth in storage change:', error)
        setShortCode('')
      }
    }

    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [])

  return encodeURIComponent(shortCode)
}

export const useLocalStorageString = (key: string) => {
  const [value, setValue] = useState<string>(() => {
    const storedValue = localStorage.getItem(key)
    return storedValue ?? ''
  })

  useEffect(() => {
    const handleStorageChange = () => {
      const newValue = localStorage.getItem(key)
      setValue(newValue ?? '')
    }

    window.addEventListener('storage', handleStorageChange)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [key])

  return value
}

// Hook to access has_changed_password
export const useHasChangedPassword = () => {
  const [hasChangedPassword, setHasChangedPassword] = useState<boolean>(() => {
    // Check osAuth first
    const osAuth = localStorage.getItem('osAuth')
    if (osAuth && osAuth !== 'undefined') {
      try {
        return JSON.parse(osAuth).has_changed_password || false
      } catch {
        return false
      }
    }
    
    // Fallback to vbAuth
    const vbAuth = localStorage.getItem('vbAuth')
    if (vbAuth && vbAuth !== 'undefined') {
      try {
        const parsed = JSON.parse(vbAuth)
        return parsed.has_changed_password || parsed.metadata?.has_changed_password || false
      } catch {
        return false
      }
    }
    
    return false
  })

  useEffect(() => {
    const handleStorageChange = () => {
      // Check osAuth first
      const osAuth = localStorage.getItem('osAuth')
      if (osAuth && osAuth !== 'undefined') {
        try {
          setHasChangedPassword(JSON.parse(osAuth).has_changed_password || false)
          return
        } catch {
          setHasChangedPassword(false)
          return
        }
      }
      
      // Fallback to vbAuth
      const vbAuth = localStorage.getItem('vbAuth')
      if (vbAuth && vbAuth !== 'undefined') {
        try {
          const parsed = JSON.parse(vbAuth)
          setHasChangedPassword(parsed.has_changed_password || parsed.metadata?.has_changed_password || false)
        } catch {
          setHasChangedPassword(false)
        }
      } else {
        setHasChangedPassword(false)
      }
    }

    window.addEventListener('storage', handleStorageChange)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [])

  return hasChangedPassword
}

// Hook to access features
export const useFeatures = () => {
  const [features, setFeatures] = useState<string[]>(() => {
    // Check osAuth first
    const osAuth = localStorage.getItem('osAuth')
    if (osAuth && osAuth !== 'undefined') {
      try {
        return JSON.parse(osAuth).features || []
      } catch {
        return []
      }
    }
    
    // Fallback to vbAuth
    const vbAuth = localStorage.getItem('vbAuth')
    if (vbAuth && vbAuth !== 'undefined') {
      try {
        const parsed = JSON.parse(vbAuth)
        return parsed.features || []
      } catch {
        return []
      }
    }
    
    return []
  })

  useEffect(() => {
    const handleStorageChange = () => {
      // Check osAuth first
      const osAuth = localStorage.getItem('osAuth')
      if (osAuth && osAuth !== 'undefined') {
        try {
          setFeatures(JSON.parse(osAuth).features || [])
          return
        } catch {
          setFeatures([])
          return
        }
      }
      
      // Fallback to vbAuth
      const vbAuth = localStorage.getItem('vbAuth')
      if (vbAuth && vbAuth !== 'undefined') {
        try {
          const parsed = JSON.parse(vbAuth)
          setFeatures(parsed.features || [])
        } catch {
          setFeatures([])
        }
      } else {
        setFeatures([])
      }
    }

    window.addEventListener('storage', handleStorageChange)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [])

  return features
}

// Hook to access sidebar links
export const useSidebarLinks = () => {
  const [sidebarLinks, setSidebarLinks] = useState<any[]>(() => {
    // Check osAuth first
    const osAuth = localStorage.getItem('osAuth')
    if (osAuth && osAuth !== 'undefined') {
      try {
        return JSON.parse(osAuth).sidebarLinks || []
      } catch {
        return []
      }
    }
    
    // No fallback for vbAuth as it doesn't contain sidebarLinks
    return []
  })

  useEffect(() => {
    const handleStorageChange = () => {
      // Check osAuth first
      const osAuth = localStorage.getItem('osAuth')
      if (osAuth && osAuth !== 'undefined') {
        try {
          setSidebarLinks(JSON.parse(osAuth).sidebarLinks || [])
        } catch {
          setSidebarLinks([])
        }
      } else {
        setSidebarLinks([])
      }
    }

    window.addEventListener('storage', handleStorageChange)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [])

  return sidebarLinks
}

// Hook to access subscription data
export const useSubscription = () => {
  const [subscription, setSubscription] = useState<any>(() => {
    // Check osAuth first
    const osAuth = localStorage.getItem('osAuth')
    if (osAuth && osAuth !== 'undefined') {
      try {
        return JSON.parse(osAuth).subscription || {}
      } catch {
        return {}
      }
    }
    
    // Fallback to vbAuth
    const vbAuth = localStorage.getItem('vbAuth')
    if (vbAuth && vbAuth !== 'undefined') {
      try {
        const parsed = JSON.parse(vbAuth)
        return parsed.subscription || {}
      } catch {
        return {}
      }
    }
    
    return {}
  })

  useEffect(() => {
    const handleStorageChange = () => {
      // Check osAuth first
      const osAuth = localStorage.getItem('osAuth')
      if (osAuth && osAuth !== 'undefined') {
        try {
          setSubscription(JSON.parse(osAuth).subscription || {})
          return
        } catch {
          setSubscription({})
          return
        }
      }
      
      // Fallback to vbAuth
      const vbAuth = localStorage.getItem('vbAuth')
      if (vbAuth && vbAuth !== 'undefined') {
        try {
          const parsed = JSON.parse(vbAuth)
          setSubscription(parsed.subscription || {})
        } catch {
          setSubscription({})
        }
      } else {
        setSubscription({})
      }
    }

    window.addEventListener('storage', handleStorageChange)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [])

  return subscription
}

// Hook to access business data
export const useBusinessData = () => {
  const [business, setBusiness] = useState<any>(() => {
    // Check osAuth first
    const osAuth = localStorage.getItem('osAuth')
    if (osAuth && osAuth !== 'undefined') {
      try {
        return JSON.parse(osAuth).business || {}
      } catch {
        return {}
      }
    }
    
    // Fallback to vbAuth
    const vbAuth = localStorage.getItem('vbAuth')
    if (vbAuth && vbAuth !== 'undefined') {
      try {
        const parsed = JSON.parse(vbAuth)
        return parsed.business || {}
      } catch {
        return {}
      }
    }
    
    return {}
  })

  useEffect(() => {
    const handleStorageChange = () => {
      // Check osAuth first
      const osAuth = localStorage.getItem('osAuth')
      if (osAuth && osAuth !== 'undefined') {
        try {
          setBusiness(JSON.parse(osAuth).business || {})
          return
        } catch {
          setBusiness({})
          return
        }
      }
      
      // Fallback to vbAuth
      const vbAuth = localStorage.getItem('vbAuth')
      if (vbAuth && vbAuth !== 'undefined') {
        try {
          const parsed = JSON.parse(vbAuth)
          setBusiness(parsed.business || {})
        } catch {
          setBusiness({})
        }
      } else {
        setBusiness({})
      }
    }

    window.addEventListener('storage', handleStorageChange)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [])

  return business
}

const originalSetItem = localStorage.setItem
localStorage.setItem = function (key, value) {
  const event = new Event('local-storage')
  originalSetItem.apply(this, [key, value])
  window.dispatchEvent(event)
}

export const useNotificationsView = () => {
  const navigate = useNavigate()
  const accountType = useLocalStorageString('accountType') as AccountType

  const redirectToNotifications = () => {
    switch (accountType) {
      case AccountType.business:
        navigate('/notifications')
        break
      case AccountType.client:
        navigate('/client-portal/notifications')
        break
      case AccountType.business_team_leader:
        navigate('/team-leader/notifications')
        break
      case AccountType.business_operations_managers:
        navigate('/operations-manager/notifications')
        break
      default:
        navigate('/notifications')
    }
  }

  return { redirectToNotifications }
}